import { useEffect, useMemo, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { BookOpen, Boxes, Compass, FlaskConical, Focus, NotebookPen, Search, Settings, Shuffle, SunMoon, Swords, Keyboard, Route, X } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import curriculumJson from '../generated/curriculum.json';
import problemsJson from '../generated/problems.json';
import type { CurriculumManifest, Problem, SearchDocument } from '@cpu/domain';
import { createSearchDocuments, searchDocuments } from '@cpu/search-engine';
import { Button, Shortcut } from '@cpu/design-system';
import { useTheme } from './theme';
import { useComfort } from './comfort';
import { useFocus } from './focusTimer';
import { playTick } from './sound';

const curriculum = curriculumJson as unknown as CurriculumManifest;
const problems = problemsJson as unknown as Problem[];

// The atom search index is large (5,931 docs); fetch it once, lazily, when the
// palette first opens instead of shipping it in the main bundle.
let atomIndexPromise: Promise<SearchDocument[]> | null = null;
function loadAtomIndex(): Promise<SearchDocument[]> {
  atomIndexPromise ??= fetch('/out/content/search-index.json')
    .then(async (response) => (response.ok ? await response.json() as SearchDocument[] : []))
    .catch(() => []);
  return atomIndexPromise;
}

export function CommandPalette({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
  const [query, setQuery] = useState('');
  const [atomDocs, setAtomDocs] = useState<SearchDocument[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const { cycleTheme } = useTheme();
  const { comfort, update } = useComfort();
  const { startSession, openLauncher } = useFocus();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(!open);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    const onOpen = () => setOpen(true);
    addEventListener('keydown', onKey);
    addEventListener('cpu:command', onOpen);
    return () => {
      removeEventListener('keydown', onKey);
      removeEventListener('cpu:command', onOpen);
    };
  }, [open, setOpen]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
      void loadAtomIndex().then((docs) => setAtomDocs(docs));
    } else {
      setQuery('');
    }
  }, [open]);

  // atomDocs must be a dependency: the lazily-fetched index lands after mount.
  const atomResults = useMemo(() => searchDocuments(atomDocs, query, 8), [atomDocs, query]);
  const results = useMemo(() => {
    const moduleAndProblemDocs = createSearchDocuments(curriculum, problems);
    return searchDocuments(moduleAndProblemDocs, query, 8);
  }, [query]);

  const go = (href: string) => {
    setOpen(false);
    void navigate({ to: href });
  };

  const run = (action: () => void) => {
    setOpen(false);
    action();
  };

  const surpriseMe = () => {
    const modules = curriculum.modules;
    const pick = modules[Math.floor(Math.random() * modules.length)]!;
    go(`/atlas/${pick.slug}`);
  };

  const startFocus = () => run(() => openLauncher());

  if (!open) return null;

  return (
    <div className="command-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}>
      <Command className="command-panel" shouldFilter={false} label="Search and commands">
        <div className="command-input-row">
          <Search size={18} aria-hidden="true" />
          <Command.Input ref={inputRef} value={query} onValueChange={setQuery} placeholder="Find an atom, concept, problem, or action…" />
          <Button variant="quiet" iconOnly aria-label="Close command palette" onClick={() => setOpen(false)}><X size={18} /></Button>
        </div>
        <Command.List className="command-list">
          <Command.Empty>No matching atom or command.</Command.Empty>
          {!query && (
            <>
              <Command.Group heading="Actions">
                <Command.Item value="Start focus block" onSelect={() => startFocus()}>
                  <Focus size={17} aria-hidden="true" /><span><b>Start a focus block</b><small>{comfort.focusPresetMinutes} min · visual countdown · logged honestly</small></span>
                </Command.Item>
                <Command.Item value="Open review session" onSelect={() => go('/review')}>
                  <BookOpen size={17} aria-hidden="true" /><span><b>Open review session</b><small>Clear due recall prompts</small></span>
                </Command.Item>
                <Command.Item value="Surprise me random module" onSelect={surpriseMe}>
                  <Shuffle size={17} aria-hidden="true" /><span><b>Surprise me</b><small>Drop into a random module — serendipity is a study strategy</small></span>
                </Command.Item>
                <Command.Item value="Toggle reading ruler" onSelect={() => { playTick(); run(() => update('ruler', comfort.ruler === 'off' ? 'line' : 'off')); }}>
                  <Route size={17} aria-hidden="true" /><span><b>Toggle reading ruler</b><small>Keep your eyes on one line at a time</small></span>
                </Command.Item>
                <Command.Item value="Keyboard shortcuts" onSelect={() => run(() => dispatchEvent(new Event('cpu:shortcuts')))}>
                  <Keyboard size={17} aria-hidden="true" /><span><b>Keyboard shortcuts</b><small>The whole system, mouseless</small></span>
                </Command.Item>
                <Command.Item value="Toggle theme" onSelect={() => { playTick(); run(cycleTheme); }}>
                  <SunMoon size={17} aria-hidden="true" /><span><b>Cycle theme</b><small>Paper → Dusk → OLED</small></span>
                </Command.Item>
                <Command.Item value="Open settings" onSelect={() => go('/settings')}>
                  <Settings size={17} aria-hidden="true" /><span><b>Settings & comfort</b><small>Text size, motion, sound, goals</small></span>
                </Command.Item>
                <Command.Item value="Open note library" onSelect={() => go('/notes')}>
                  <NotebookPen size={17} aria-hidden="true" /><span><b>Note library</b><small>Every saved note, one per atom</small></span>
                </Command.Item>
              </Command.Group>
              <Command.Group heading="Go to">
                <Command.Item value="curriculum atlas" onSelect={() => go('/atlas')}>
                  <Compass size={17} aria-hidden="true" /><span><b>Curriculum atlas</b><small>45 modules, visible prerequisites</small></span>
                </Command.Item>
                <Command.Item value="trace labs" onSelect={() => go('/learn')}>
                  <FlaskConical size={17} aria-hidden="true" /><span><b>Trace labs</b><small>{25} interactive lessons</small></span>
                </Command.Item>
                <Command.Item value="practice queue" onSelect={() => go('/practice')}>
                  <Swords size={17} aria-hidden="true" /><span><b>Practice queue</b><small>{problems.length} mapped classics</small></span>
                </Command.Item>
                <Command.Item value="window invariant lab" onSelect={() => go('/learn/m13/window-invariant')}>
                  <Boxes size={17} aria-hidden="true" /><span><b>Window-invariant lab</b><small>M13 · the flagship trace</small></span>
                </Command.Item>
              </Command.Group>
            </>
          )}
          {atomResults.length > 0 && (
            <Command.Group heading={query ? 'Atoms' : 'Recently indexed atoms'}>
              {atomResults.map((result) => (
                <Command.Item key={`atom-${result.id}`} value={`${result.title} ${result.subtitle} ${result.preview}`} onSelect={() => go(result.href)}>
                  <span className="result-type">A</span>
                  <span><b>{result.title}</b><small>{(result as SearchDocument & { moduleTitle?: string }).moduleTitle ?? result.subtitle}</small></span>
                </Command.Item>
              ))}
            </Command.Group>
          )}
          <Command.Group heading={query ? 'Modules & problems' : 'Curriculum'}>
            {results.map((result) => (
              <Command.Item key={result.id} value={`${result.title} ${result.subtitle}`} onSelect={() => go(result.href)}>
                <span className="result-type">{result.type.slice(0, 1).toUpperCase()}</span>
                <span><b>{result.title}</b><small>{result.subtitle}</small></span>
              </Command.Item>
            ))}
          </Command.Group>
        </Command.List>
        <div className="command-footer"><span><Shortcut>↑↓</Shortcut> navigate</span><span><Shortcut>Enter</Shortcut> open</span><span><Shortcut>Esc</Shortcut> close</span></div>
      </Command>
    </div>
  );
}
